// =====================================================================
// Salésol — "Scenes" menu root.
// Phone frame (desktop staff preview) + the scroll container holding every
// section in order + the detail overlay. Discrete state only lives here;
// per-frame motion is written by useScrollEngine straight to the DOM.
// =====================================================================
import { useCallback, useEffect, useRef, useState } from 'react';

import { MARK } from './lib/mark-outline.js';
import useReducedMotion from './lib/useReducedMotion.js';
import { config } from './content/config.js';
import { L } from './scenes/data/strings.js';
import { PAL, coffeeA, coffeeB, foodList, signatures, tiles, flat } from './scenes/data/adapt.js';
import useTilt from './scenes/useTilt.js';
import useScrollEngine, { buzz } from './scenes/useScrollEngine.js';
import useRoute, { resolve } from './scenes/useRoute.js';
import Entry from './scenes/Entry.jsx';
import Header from './scenes/Header.jsx';
import SignatureScene from './scenes/SignatureScene.jsx';
import ChapterOpener from './scenes/ChapterOpener.jsx';
import ListPage from './scenes/ListPage.jsx';
import FilterScene from './scenes/FilterScene.jsx';
import BackCover from './scenes/BackCover.jsx';
import Detail from './scenes/Detail.jsx';

const FS = [16, 18, 20]; // A · A+ · A++ (root font size of the app)

function readFs() {
  try { return FS[Number(localStorage.getItem('salesol-font-scale')) || 0] || 16; } catch (_) { return 16; }
}
function readLang() {
  try { return localStorage.getItem('salesol-lang') === 'id' ? 'id' : 'en'; } catch (_) { return 'en'; }
}

export default function MenuApp() {
  const rm = useReducedMotion();
  const [lang, setLangState] = useState(readLang);
  const [fs, setFsState] = useState(readFs);
  const [ui, setUi] = useState({ night: true, tab: 'sig', hdBg: null });
  const [detail, setDetail] = useState(null); // { pg, ci, li, kind }
  const [dVis, setDVis] = useState(0);
  const [dOn, setDOn] = useState('0');
  const [detailGL, setDetailGL] = useState(false);

  const frameRef = useRef(null);
  const appRef = useRef(null);
  const canvasRef = useRef(null);
  const glRef = useRef(null);
  const timers = useRef({});
  const skipIntro = useRef(false);
  // The section a deep link lands on: its hero is fetched with priority,
  // every other scene's hero waits until it is near the viewport.
  const landing = useRef(resolve(location.pathname)).current;

  const T = L[lang];
  const tiltSrc = useTilt(frameRef);
  const engine = useScrollEngine({ frameRef, appRef, rm, tiltSrc, onUi: setUi, glRef });

  // Data (adapted once per render — cheap, ~60 rows).
  const sigs = signatures();
  const pagesA = coffeeA();
  const pagesB = coffeeB();
  const food = foodList();

  // Route ↔ scroll. `skipIntro` is flipped before the first paint on deep links.
  useRoute({ engine, skipIntro: () => { skipIntro.current = true; } });

  // Every React commit may have changed the DOM the engine caches.
  useEffect(() => { engine.invalidate(); });
  // Dev-only handle for driving frames from the console / test scripts.
  useEffect(() => { if (import.meta.env.DEV) window.__engine = engine; }, [engine]);

  // Persisted controls.
  const setLang = useCallback((l) => { setLangState(l); try { localStorage.setItem('salesol-lang', l); } catch (_) { /* ignore */ } }, []);
  const setFs = useCallback((px) => { setFsState(px); try { localStorage.setItem('salesol-font-scale', String(FS.indexOf(px))); } catch (_) { /* ignore */ } }, []);

  // three.js stage — lazy, after first paint, never on save-data connections.
  useEffect(() => {
    if (navigator.connection && navigator.connection.saveData) return undefined;
    let cancelled = false;
    const start = async () => {
      try {
        const [THREE, { createGlStage }] = await Promise.all([import('three'), import('./gl/GlStage.js')]);
        if (cancelled || !canvasRef.current) return;
        glRef.current = createGlStage(THREE, canvasRef.current, { rm, config });
        if (detail && detail.kind === 'lot') glRef.current.globe.setGlobe(detail.pg.cats[detail.ci].lots[detail.li]);
      } catch (_) { /* the menu is complete without WebGL */ }
    };
    // Two seconds after the load event, then idle: 171 KB of three.js must
    // never share the wire with the first paint's photo and fonts (the entry
    // intro holds the guest for 2.7 s anyway).
    let id = 0, t = 0;
    const idle = () => { id = window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 2500 }) : window.setTimeout(start, 800); };
    const later = () => { t = window.setTimeout(idle, 2000); };
    if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener('load', later);
      window.clearTimeout(t);
      if (id) { if (window.cancelIdleCallback) window.cancelIdleCallback(id); else window.clearTimeout(id); }
      if (glRef.current) { glRef.current.dispose(); glRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (glRef.current) glRef.current.rm = rm; }, [rm]);

  // ---- Detail overlay state machine (prototype 468–474) ----
  const clearTimers = () => { Object.values(timers.current).forEach(clearTimeout); timers.current = {}; };
  const lotOf = (d) => d.pg.cats[d.ci].lots[d.li];

  const openDetail = useCallback((pg, ci, li) => {
    const kind = pg.chap === 'chapCoffee' ? 'lot' : 'dish';
    clearTimers();
    buzz(8);
    engine.detailOpen = true;
    if (engine.syncUrl) engine.syncUrl();
    try { history.pushState({ detail: true }, '', location.pathname); } catch (_) { /* ignore */ }
    const d = { pg, ci, li, kind };
    setDetail(d); setDVis(0); setDOn('0'); setDetailGL(false);
    if (glRef.current) glRef.current.globe.spinV = 0;
    timers.current.t1 = setTimeout(() => setDVis(1), 20);
    timers.current.t2 = setTimeout(() => setDOn('1'), 140);
    if (kind === 'lot') {
      if (glRef.current) glRef.current.globe.setGlobe(lotOf(d));
      timers.current.t3 = setTimeout(() => {
        if (!glRef.current || !glRef.current.globe) return;
        glRef.current.globe.t0 = performance.now();
        if (appRef.current) appRef.current.style.visibility = 'hidden';
        engine.detailGL = true;
        setDetailGL(true);
      }, 460);
    }
  }, [engine]);

  const finishClose = useCallback(() => {
    clearTimers();
    if (appRef.current) appRef.current.style.visibility = '';
    engine.detailGL = false;
    engine.detailOpen = false;
    setDetailGL(false); setDVis(0); setDOn('0');
    timers.current.t1 = setTimeout(() => setDetail(null), 440);
  }, [engine]);

  const closeDetail = useCallback(() => {
    if (history.state && history.state.detail) { history.back(); return; } // popstate → finishClose
    finishClose();
  }, [finishClose]);

  useEffect(() => {
    const onPop = () => { if (engine.detailOpen) finishClose(); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [engine, finishClose]);

  const stepDetail = useCallback((dir) => {
    setDetail((d) => {
      if (!d) return d;
      buzz(5);
      const items = flat(d.pg);
      const i = items.findIndex((x) => x.ci === d.ci && x.li === d.li);
      const n = items[(i + dir + items.length) % items.length];
      setDOn('0');
      clearTimeout(timers.current.t2);
      timers.current.t2 = setTimeout(() => {
        setDetail({ ...d, ci: n.ci, li: n.li });
        setDOn('1');
        if (d.kind === 'lot' && glRef.current) glRef.current.globe.setGlobe(n.c);
      }, 240);
      return d;
    });
  }, []);

  // Lock the page behind an open overlay.
  useEffect(() => {
    const app = appRef.current; if (!app) return undefined;
    app.style.overflowY = detail ? 'hidden' : 'auto';
    return () => { app.style.overflowY = 'auto'; };
  }, [detail]);

  return (
    <div className="scenes">
      <div data-shell>
        <svg aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
          <symbol id="salesol-mark" viewBox="0 0 1000 1000"><path fill="currentColor" d={MARK.svgPath} /></symbol>
        </svg>
        <div data-frame ref={frameRef}>
          <canvas data-gl ref={canvasRef} aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', zIndex: 0 }} />
          <div
            data-app
            ref={appRef}
            style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', overflowY: 'auto', overflowX: 'hidden', scrollSnapType: 'y proximity', color: '#f4f0e8', fontSize: fs + 'px', lineHeight: 1.35, overscrollBehavior: 'contain' }}
          >
            <Entry T={T} rm={rm} skipIntro={skipIntro} deep={landing !== 's-top'} />
            <Header T={T} lang={lang} setLang={setLang} fs={fs} setFs={setFs} night={ui.night} tab={ui.tab} hdBg={ui.hdBg} engine={engine} />
            {sigs.map((s, i) => <SignatureScene key={s.id} sig={s} index={i} T={T} priority={s.id === landing} />)}
            <ChapterOpener kind="beans" T={T} beans={config.beans} />
            {pagesA.map((pg) => <ListPage key={pg.id} pg={pg} pal={PAL.coffee} T={T} lang={lang} onOpen={openDetail} />)}
            <FilterScene T={T} engine={engine} priority={landing === 's-filter'} />
            {pagesB.map((pg) => <ListPage key={pg.id} pg={pg} pal={pg.tone === 'night' ? PAL.night : PAL.coffee} T={T} lang={lang} onOpen={openDetail} />)}
            <ChapterOpener kind="table" T={T} tiles={tiles()} engine={engine} />
            {food.map((pg) => <ListPage key={pg.id} pg={pg} pal={pg.tea ? PAL.tea : PAL.food} T={T} lang={lang} onOpen={openDetail} priority={pg.id === landing} />)}
            <BackCover T={T} />
          </div>
          {detail && (
            <Detail
              detail={detail} T={T} fs={fs} vis={dVis} on={dOn} gl={detailGL}
              onClose={closeDetail} onStep={stepDetail} glRef={glRef}
            />
          )}
        </div>
      </div>
    </div>
  );
}
